<script setup>
import { ref, onMounted, watch } from 'vue';
import SearchFields from './SearchFields.vue';
import Swal from 'sweetalert2';
import axios from 'axios';

const props = defineProps(['fetchUrl', 'deleteUrl', 'completeUrl', 'addKeyUrl', 'confirmUrl', 'status']);
const requestList = ref([]);
const isLoading = ref(false);
const selectedRequest = ref(null);
const newRfid = ref('');
const selectedChargeRate = ref('');
const addKeyError = ref('');

function buildUrl(template, requestId) {
    if (!template) return null;

    return template
        .replace('__ID__', requestId)
        .replace('{id}', requestId)
        .replace(':id', requestId);
}

function buildCompleteUrl(requestId) {
    if (props.completeUrl) {
        return buildUrl(props.completeUrl, requestId);
    }

    if (!props.fetchUrl) return null;

    const baseUrl = props.fetchUrl.split('?')[0].replace(/\/$/, '');
    const normalizedUrl = baseUrl.replace(/\/(all|requested|confirmed|completed)$/, '');

    return `${normalizedUrl}/${requestId}/complete`;
}

const filtersAsString = ref('');
const receivedFilterParameters = ref([]);

// Suchfelder-Konfiguration (Teilnehmer umbenannt)
const filterParameters = [
    {index: 'rechnungsempfaenger', title: 'Kundennummer', value: null},
    {index: 'teilnehmer', title: 'Teilnehmer', value: null},
    {index: 'name1', title: 'Nachname', value: null},
    {index: 'name2', title: 'Vorname', value: null},
];

const loadRequests = async () => {
    isLoading.value = true;
    try {
        let url = props.fetchUrl;
        if (filtersAsString.value) {
            url += `?filter=${encodeURIComponent(filtersAsString.value)}`;
        }
        const response = await axios.get(url);
        requestList.value = response.data.data;
    } catch (error) {
        console.error('Fehler beim Laden der Mobility-Key Anfragen', error);
    } finally {
        isLoading.value = false;
    }
};

onMounted(() => {
    loadRequests();
});

watch(() => props.fetchUrl, () => {
    loadRequests();
});

function filterSearch(givenFields) {
    receivedFilterParameters.value = [];
    for (let i = 0; i < givenFields.length; i++) {
        if (givenFields[i].value !== null && givenFields[i].value.trim() !== '') {
            receivedFilterParameters.value.push(givenFields[i]);
        }
    }
    filtersAsString.value = JSON.stringify(receivedFilterParameters.value);
    loadRequests();
}

async function printAnschreiben(request) {
    const result = await Swal.fire({
        text: 'Bitte sicherstellen, dass der Mobility-Key bei Lisy angelegt wurde!',
        icon: 'info'
    });

    if (!result.isConfirmed) return;

    const url = `/teilnehmer/letter/${request.rechnungsempfaenger}/${request.teilnehmer}/true`;
    window.open(url, '_blank');
}

async function deleteRequest(request) {
    const confirmResult = await Swal.fire({
        title: 'Anfrage löschen?',
        text: `Soll die Anfrage für Teilnehmer ${request.teilnehmer} wirklich gelöscht werden?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Löschen',
        cancelButtonText: 'Abbrechen',
        confirmButtonColor: '#d33'
    });

    if (!confirmResult.isConfirmed) return;

    try {
        isLoading.value = true;
        const url = buildUrl(props.deleteUrl, request.id);
        const response = await axios.get(url);
        if (response.status === 200) {
            Swal.fire('Erfolgreich', 'Die Anfrage wurde gelöscht.', 'success');
            await loadRequests();
        }
    } catch (e) {
        Swal.fire('Fehler', 'Die Anfrage konnte nicht gelöscht werden.', 'error');
    } finally {
        isLoading.value = false;
    }
}
async function confirmManualKey(request) {
    const confirmResult = await Swal.fire({
        title: 'Mobility-Key manuell bestätigen?',
        text: `Soll der Mobility-Key für Teilnehmer ${request.teilnehmer} manuell bestätigt werden?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Bestätigen',
        cancelButtonText: 'Abbrechen'
    });

    if (!confirmResult.isConfirmed) return;

    try {
        isLoading.value = true;
        const response = await axios.post(props.confirmUrl, {
            Rechnungsempfaengernummer: request.rechnungsempfaenger,
            Teilnehmer: request.teilnehmer
        });

        if (response.status === 200) {
            Swal.fire('Erfolgreich', 'Mobility-Key wurde manuell bestätigt.', 'success');
            await loadRequests();
        }
    } catch (e) {
        Swal.fire('Fehler', e.response?.data?.error || 'Die manuelle Bestätigung ist fehlgeschlagen.', 'error');
    } finally {
        isLoading.value = false;
    }
}
async function markAsComplete(request) {
    const confirmResult = await Swal.fire({
        title: 'Versand abschließen?',
        text: `Soll die Anfrage für Teilnehmer ${request.teilnehmer} als versendet markiert werden?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Als versendet markieren',
        cancelButtonText: 'Abbrechen'
    });

    if (!confirmResult.isConfirmed) return;

    try {
        isLoading.value = true;
        const url = buildCompleteUrl(request.id);
        const response = await axios.post(url);
        if (response.status === 200) {
            Swal.fire('Erfolgreich', 'Vorgang abgeschlossen.', 'success');
            await loadRequests();
        }
    } catch (e) {
        Swal.fire('Fehler', 'Aktion konnte nicht abgeschlossen werden.', 'error');
    } finally {
        isLoading.value = false;
    }
}

function openAddKeyModal(request) {
    selectedRequest.value = request;
    newRfid.value = '';
    selectedChargeRate.value = '';
    addKeyError.value = '';
    new bootstrap.Modal(document.getElementById('addMobilityKeyModal')).show();
}

async function addMobilityKey() {
    if (!selectedRequest.value) return;
    const request = selectedRequest.value;
    if (!newRfid.value.trim()) {
        addKeyError.value = 'Bitte geben Sie eine RFID-Nummer ein.';
        return;
    }
    if (!selectedChargeRate.value) {
        addKeyError.value = 'Bitte wählen Sie einen Ladetarif aus.';
        return;
    }

    try {
        isLoading.value = true;
        addKeyError.value = '';
        const url = buildUrl(props.addKeyUrl, selectedRequest.value.id);
        const response = await axios.post(url, {
            rfid: newRfid.value.trim(),
            charging_rate_id: selectedChargeRate.value
        });

        if (response.status === 200) {
            request.key_added = true;
            const requestInList = requestList.value.find(item => item.id === request.id);
            if (requestInList) requestInList.key_added = true;

            bootstrap.Modal.getInstance(document.getElementById('addMobilityKeyModal')).hide();
            selectedRequest.value = null;
            await loadRequests();

            const result = await Swal.fire({
                title: 'Mobility-Key angelegt',
                text: 'Möchten Sie jetzt auch das Anschreiben generieren?',
                icon: 'success',
                showCancelButton: true,
                confirmButtonText: 'Anschreiben generieren',
                cancelButtonText: 'Später'
            });

            if (result.isConfirmed) {
                printAnschreiben(request);
            }
        }
    } catch (error) {
        const data = error.response?.data || {};
        addKeyError.value = data.errors?.rfid?.[0]
            || data.errors?.charging_rate_id?.[0]
            || data.error
            || 'Der Mobility-Key konnte nicht hinzugefügt werden.';
        if (error.response?.status !== 422) {
            Swal.fire('Fehler', addKeyError.value, 'error');
        }
    } finally {
        isLoading.value = false;
    }
}
</script>

<template>
    <!-- Suchmaske -->
    <SearchFields
        :fields="filterParameters"
        @filters="filterSearch"
    />

    <div class="main-table mt-4" v-if="requestList.length > 0">
        <div class="inner-table">
            <table class="table">
                <thead>
                    <tr>
                        <th class="table-head">Erstellt am</th>
                        <th class="table-head">Kundennr.</th>
                        <th class="table-head">Kunde Name</th>
                        <th class="table-head">Teilnehmer ID</th>
                        <th class="table-head">Teilnehmer Name</th>
                        <th class="table-head">Status</th>
                        <th class="table-head" style="text-align: center;">Aktionen</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="req in requestList" :key="req.id" class="m-datatable__row">
                        <td>{{ req.date_formatted }}</td>
                        <td>{{ req.rechnungsempfaenger }}</td>
                        <td>{{ req.re_name }}</td>
                        <td>{{ req.teilnehmer }}</td>
                        <td>{{ req.tn_name }}</td>
                        <td>
                            <span v-if="req.status === 'requested'"  style="color: #212529 !important;">Wartet auf Bestätigung</span>
                            <span v-else-if="req.status === 'confirmed'"  style="color: #212529 !important;">Bereit zum Senden</span>
                            <span v-else-if="req.status === 'completed'"  style="color: #212529 !important;">Versendet</span>
                        </td>
                        <td>
                            <div class="d-flex" style="gap: 0.09em !important; justify-content: flex-end;">
                                <button v-if="req.status === 'confirmed' && !req.key_added" style="min-width: 13em;text-align: left" class="btn btn-primary btn-sm" @click="openAddKeyModal(req)" :disabled="isLoading">
                                    <i class="fa-solid fa-key me-1"></i> &#10240;Mobility-Key anlegen
                                </button>
                                <button v-if="req.status === 'confirmed' && req.key_added" class="btn btn-success btn-sm" @click="markAsComplete(req)" :disabled="isLoading">
                                    <i class="fa-solid fa-check me-1"></i> Versand abschließen
                                </button>
                                <button v-if="req.key_added && (req.status === 'confirmed' || req.status === 'completed')"  style="min-width: 13em;" class="btn btn-primary btn-sm" @click="printAnschreiben(req)">
                                    <i class="fa-solid fa-print me-1"></i> Anschreiben generieren
                                </button>
                                <button v-if=" req.status !== 'completed' && req.status !== 'confirmed'" class="btn btn-primary btn-sm" @click="confirmManualKey(req)" :disabled="isLoading">
                                   Manuell bestätigen
                                </button>



                                <button @click="deleteRequest(req)" :disabled="isLoading"
                                id="loesche" type="button" class="btn btn-sm btn-primary" style="
                                   background-color: rgb(221, 51, 51);border-color: rgb(221, 51, 51);">
                                    Löschen
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="m-3" v-else-if="!isLoading">
        <div class="alert alert-custom" role="alert">
            <span>In dieser Kategorie liegen aktuell keine Anfragen vor.</span>
        </div>
    </div>

    <div class="modal fade" id="addMobilityKeyModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Mobility-Key hinzufügen</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
                </div>
                <div class="modal-body" v-if="selectedRequest">
                    <p>Mobility-Key für Teilnehmer {{ selectedRequest.teilnehmer }} hinzufügen. <b>Bitte Mobility-Key bei Lisy anlegen!</b></p>
                    <div class="mb-3">
                        <label for="mobilityKeyRfid" class="form-label"><strong>RFID-Nummer</strong></label>
                        <input
                            id="mobilityKeyRfid"
                            v-model="newRfid"
                            type="text"
                            class="form-control"
                            maxlength="255"
                            autocomplete="off"
                        >
                    </div>
                    <div class="mb-3">
                        <label for="mobilityKeyRate" class="form-label"><strong>Ladetarif</strong></label>
                        <select id="mobilityKeyRate" v-model="selectedChargeRate" class="form-select">
                            <option value="" disabled>Bitte wählen...</option>
                            <option value="2">Mobility Key Aachen</option>
                            <option value="7">Mobility Key Aachen Park-Flat</option>
                            <option value="5">Mobility Key Berlin</option>
                            <option value="3">Mobility Key Plus</option>
                            <option value="4">Mobility Key Flat</option>
                        </select>
                    </div>
                    <div v-if="addKeyError" class="alert alert-danger" role="alert">
                        {{ addKeyError }}
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Abbrechen</button>
                    <button type="button" class="btn btn-primary" @click="addMobilityKey" :disabled="isLoading">
                        <i v-if="isLoading" class="fa-solid fa-spinner fa-spin me-1"></i>
                        RFID hinzufügen
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
